import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import {
  getCloudinaryCoverPhoto,
  getCloudinaryImageUrl,
  getCoverPhoto,
} from '../../util/listingImages';
import { APPLY_FOR_INVITE_URL } from '../../util/applyForInvite';
import { isBookingProcessAlias } from '../../transactions/transaction';
import {
  AspectRatioWrapper,
  AvatarLarge,
  NamedLink,
  ResponsiveImage,
} from '..';

import css from './ListingCard.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    config.currency,
    intl
  );
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="ListingCard.perUnit"
            values={{ unitType: publicData?.unitType }}
          />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isAuthenticated,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const {
    title = '',
    description,
    price,
    publicData,
  } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const {
    category,
    city,
    listingType,
    location,
    NumberofBedrooms,
    NumberofBathrooms,
    bedTypes,
    maximumGuests,
    ageSuitability,
    transportationAvailable,
    cloudnaryImage = [],
  } = publicData || {};
  const firstImage = getCoverPhoto(currentListing.images, publicData);
  const cloudinaryCoverPhotoUrl = getCloudinaryImageUrl(
    getCloudinaryCoverPhoto(publicData)
  );
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k =>
        k.startsWith(variantPrefix)
      )
    : [];
  const formatAgeRange = value => {
    if (!value) return '';

    // If value is an array, use the first value
    const valueToFormat = Array.isArray(value) ? value[0] : value;

    // Convert to string if it's not already
    const stringValue = String(valueToFormat);

    const match = stringValue.match(/^(\d+)_*(\d+)*_years$/);
    if (match) {
      const [, from, to] = match;
      return to ? `${from}-${to}` : from;
    }
    return stringValue;
  };
  const ageDisplay = formatAgeRange(ageSuitability);
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const getLocationLabel = address => {
    if (!address) return '';

    const parts = address
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);

    if (parts.length < 2) {
      return address;
    }

    const country = parts[parts.length - 1];
    let stateOrRegion = parts.length >= 2 ? parts[parts.length - 2] : '';
    const city = parts.length >= 3 ? parts[parts.length - 3] : '';

    // Remove postal codes from state/region
    // Remove US ZIP codes (e.g., 12345 or 12345-6789)
    stateOrRegion = stateOrRegion
      .replace(/\b\d{5}(?:-\d{4})?\b/g, '')
      // Remove Canadian postal codes (e.g., A0G 3E0)
      .replace(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/gi, '')
      // Remove UK postal codes (e.g., SW1A 1AA)
      .replace(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/gi, '')
      // Remove other common postal code patterns
      .replace(/\b\d{4,6}\b/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      // Remove leading postal codes not caught above (e.g., "841 00 Ermoupolis" → "Ermoupolis")
      .replace(/^\d[\d\s]*\s+/, '')
      .trim();

    // Check if it's USA
    const usaCountries = [
      'United States',
      'United States of America',
      'USA',
      'US',
    ];

    const isUSA = usaCountries.some(
      c => c.toLowerCase() === country.toLowerCase()
    );

    if (isUSA && city && stateOrRegion) {
      // USA: show "City, State"
      return `${city}, ${stateOrRegion}`;
    }

    if (!isUSA && city && country) {
      // International: show "City, Country"
      // If the parsed "city" contains digits it's a street (e.g. "Psaron Street 22"),
      // so use stateOrRegion (which is the actual city) instead.
      if (/\d/.test(city)) {
        return stateOrRegion ? `${stateOrRegion}, ${country}` : country;
      }
      return `${city}, ${country}`;
    }

    // Fallbacks
    if (city && stateOrRegion) {
      return `${city}, ${stateOrRegion}`;
    }
    if (city) return city;
    if (stateOrRegion) return `${stateOrRegion}, ${country}`;
    return country || address;
  };

  const locationShow = getLocationLabel(location?.address);
  return (
    <>
      {isAuthenticated ? (
        <NamedLink
          className={css.listCard}
          name="ListingPage"
          params={{ id, slug }}
        >
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            {cloudinaryCoverPhotoUrl ? (
              <LazyImage
                rootClassName={css.rootForImage}
                alt={title}
                url={cloudinaryCoverPhotoUrl}
                variants={[variantPrefix]}
                sizes={renderSizes}
              />
            ) : (
              <LazyImage
                rootClassName={css.rootForImage}
                alt={title}
                image={firstImage}
                variants={variants}
                sizes={renderSizes}
              />
            )}
          </AspectRatioWrapper>
          <div className={css.searchInfoContainer}>
            <div className={css.countryWrapper}>
              <AvatarLarge
                className={css.avatar}
                user={author}
                disableProfileLink
              />
              <button className={css.countryButton}>{locationShow}</button>
            </div>
            <div className={css.totalAmount}>
              {/* <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} /> */}
              ${Number(price?.amount || 0).toLocaleString('en-US')} Monthly
              {/* ${price?.amount}.00 <span className={css.monthlyBox}></span> */}
            </div>
            <div className={css.mainInfo}>
              {/* <div className={css.searchTitle}> {richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}</div> */}
              <div className={css.searchTitle}>
                <span>{description}</span>
              </div>

              <div className={css.buttonsWrapper}>
                <button className={css.searchButtons}>
                  <IconCard brand="bed" />

                  {/* {transportationAvailable?.length} */}
                  {NumberofBedrooms}
                  <span className={css.methodName}>Bedrooms</span>
                </button>
                <button className={css.searchButtons}>
                  <IconCard brand="toy" />
                  {/* {bedTypes?.length} */}
                  {NumberofBathrooms}
                  <span className={css.methodName}>Bathrooms</span>
                </button>

                <button className={css.searchButtons}>
                  <IconCard brand="familyInput" />
                  {maximumGuests} Guests
                </button>
              </div>
            </div>
            {/* <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div> */}
          </div>
        </NamedLink>
      ) : (
        <a
          className={css.listCardBlurred}
          href={APPLY_FOR_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            {cloudinaryCoverPhotoUrl ? (
              <LazyImage
                rootClassName={css.rootForImage}
                alt={title}
                url={cloudinaryCoverPhotoUrl}
                variants={[variantPrefix]}
                sizes={renderSizes}
              />
            ) : (
              <LazyImage
                rootClassName={css.rootForImage}
                alt={title}
                image={firstImage}
                variants={variants}
                sizes={renderSizes}
              />
            )}
          </AspectRatioWrapper>
          <div className={css.searchInfoContainer}>
            <div className={css.countryWrapper}>
              <AvatarLarge
                className={css.avatar}
                user={author}
                disableProfileLink
              />
              <button className={css.countryButton}>{locationShow}</button>
            </div>
            <div className={css.totalAmount}>
              <PriceMaybe
                price={price}
                publicData={publicData}
                config={config}
                intl={intl}
              />
            </div>
            <div className={css.mainInfo}>
              {/* <div className={css.searchTitle}> {richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}</div> */}
              <div className={css.searchTitle}>
                <span>{description}</span>
              </div>

              <div className={css.buttonsWrapper}>
                <button className={css.searchButtons}>
                  <IconCard brand="bed" />

                  {/* {transportationAvailable?.length} */}
                  {transportationAvailable?.length}
                  <span className={css.methodName}>Transport</span>
                </button>
                <button className={css.searchButtons}>
                  <IconCard brand="toy" />
                  {/* {bedTypes?.length} */}
                  {NumberofBedrooms}
                  <span className={css.methodName}>Bedrooms</span>
                </button>

                <button className={css.searchButtons}>
                  <IconCard brand="familyInput" />
                  {maximumGuests} Guests
                </button>
              </div>
            </div>
            {/* <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div> */}
          </div>
        </a>
      )}
    </>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
